import { useCallback, useContext, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { ControlGestureContext } from './ControlGestureContext';
import { boundedValue, holdRate, motionValue, nudgeSteps, pushAmount, pushRate, MOTION_INTERVAL_MS, type NumericScale } from './numeric-motion';

interface Properties extends NumericScale { value: number; disabled: boolean; onChange: (value: number) => void }
interface Motion {
  kind: 'hold' | 'push'; direction: number; pointer?: number; key?: string;
  target: HTMLButtonElement; x: number; amount: number; started: number; last: number;
  value: number; origin: number; emitted: number; fine: number;
}
const neutral = { active: false, amount: 0, fast: false };

/** One rate-based gesture, stopped by release, cancellation, blur, hiding or unmount. */
export function useNumericMotion(properties: Properties) {
  const group = useContext(ControlGestureContext);
  const latest = useRef({ properties, group }); latest.current = { properties, group };
  const held = useRef<Motion | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const suppressClickUntil = useRef(0);
  const [visual, setVisual] = useState(neutral);

  const stop = useCallback((paint = true) => {
    if (timer.current !== undefined) clearTimeout(timer.current);
    timer.current = undefined;
    const gesture = held.current; held.current = null;
    if (gesture) {
      suppressClickUntil.current = performance.now() + 100;
      if (gesture.pointer !== undefined && gesture.target.hasPointerCapture?.(gesture.pointer)) gesture.target.releasePointerCapture(gesture.pointer);
      latest.current.group.end();
    }
    if (paint) setVisual(neutral);
  }, []);

  const tick = () => {
    const gesture = held.current; if (!gesture) return;
    const props = latest.current.properties;
    if (props.disabled) { stop(); return; }
    const now = performance.now();
    const rate = (gesture.kind === 'hold' ? gesture.direction * holdRate(now - gesture.started, props) : pushRate(gesture.amount, props)) * gesture.fine;
    const seconds = Math.max(0, Math.min(.1, (now - gesture.last) / 1000));
    gesture.last = now;
    gesture.value = boundedValue(gesture.value + rate * seconds, props);
    const value = motionValue(gesture.value, gesture.origin, props);
    if (value !== gesture.emitted) { gesture.emitted = value; props.onChange(value); }
    const amount = gesture.kind === 'push' ? gesture.amount : gesture.direction;
    setVisual({ active: true, amount, fast: gesture.fine >= 1 && (gesture.kind === 'hold' ? now - gesture.started >= 800 : Math.abs(amount) >= .65) });
    timer.current = setTimeout(tick, MOTION_INTERVAL_MS);
  };

  const begin = (target: HTMLButtonElement, kind: Motion['kind'], direction: number, pointer?: number, key?: string, fine = 1, x = 0) => {
    if (latest.current.properties.disabled || held.current) return;
    const props = latest.current.properties;
    const now = performance.now();
    const initial = kind === 'hold' ? boundedValue(props.value + direction * props.step * (fine < 1 ? 1 : nudgeSteps(props)), props) : props.value;
    held.current = { target, kind, direction, x, amount: 0, started: now, last: now, origin: props.value, value: initial, emitted: initial, fine,
      ...(pointer === undefined ? {} : { pointer }), ...(key === undefined ? {} : { key }) };
    latest.current.group.begin();
    if (initial !== props.value) props.onChange(initial);
    setVisual({ active: true, amount: direction, fast: false });
    timer.current = setTimeout(tick, MOTION_INTERVAL_MS);
  };

  useEffect(() => {
    const release = (event: globalThis.PointerEvent) => { if (held.current?.pointer === event.pointerId) stop(); };
    const blur = () => stop();
    const hidden = () => { if (document.hidden) stop(); };
    const key = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Shift' && held.current) held.current.fine = .1;
      if (event.key === 'Escape' || (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') stop();
    };
    const keyRelease = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Shift' && held.current) held.current.fine = 1;
      if (held.current?.key === event.key) stop();
    };
    window.addEventListener('pointerup', release); window.addEventListener('pointercancel', release);
    window.addEventListener('blur', blur); window.addEventListener('keydown', key); window.addEventListener('keyup', keyRelease); document.addEventListener('visibilitychange', hidden);
    return () => {
      window.removeEventListener('pointerup', release); window.removeEventListener('pointercancel', release);
      window.removeEventListener('blur', blur); window.removeEventListener('keydown', key); window.removeEventListener('keyup', keyRelease); document.removeEventListener('visibilitychange', hidden); stop(false);
    };
  }, [stop]);
  useEffect(() => { if (properties.disabled) stop(); }, [properties.disabled, stop]);
  useEffect(() => {
    if (held.current && Math.abs(properties.value - held.current.emitted) > Math.max(.001, properties.step / 10)) stop();
  }, [properties.value, properties.step, stop]);

  const pointerDown = (event: PointerEvent<HTMLButtonElement>, kind: Motion['kind'], direction = 0) => {
    if (event.button !== 0 || held.current || latest.current.properties.disabled) return;
    event.preventDefault(); event.currentTarget.focus({ preventScroll: true });
    event.currentTarget.setPointerCapture?.(event.pointerId);
    begin(event.currentTarget, kind, direction, event.pointerId, undefined, event.shiftKey ? .1 : 1, event.clientX);
  };
  const pointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const gesture = held.current;
    if (!gesture || gesture.kind !== 'push' || gesture.pointer !== event.pointerId) return;
    gesture.amount = pushAmount(event.clientX - gesture.x);
    gesture.fine = event.shiftKey ? .1 : 1;
    setVisual({ active: true, amount: gesture.amount, fast: gesture.fine >= 1 && Math.abs(gesture.amount) >= .65 });
  };
  const release = () => stop();
  const keyDown = (event: KeyboardEvent<HTMLButtonElement>, direction?: number) => {
    if (latest.current.properties.disabled) return;
    if (event.key === 'Escape') { stop(); return; }
    const isLever = direction === undefined;
    const arrow = event.key === 'ArrowLeft' || event.key === 'ArrowDown' ? -1 : event.key === 'ArrowRight' || event.key === 'ArrowUp' ? 1 : undefined;
    const accepted = isLever ? arrow !== undefined : event.key === ' ' || event.key === 'Enter';
    if (isLever && (event.key === 'Home' || event.key === 'End')) {
      event.preventDefault(); event.stopPropagation(); stop(); latest.current.group.begin();
      latest.current.properties.onChange(event.key === 'Home' ? properties.min : properties.max); latest.current.group.end(); return;
    }
    if (!accepted) return;
    event.preventDefault(); event.stopPropagation();
    if (!held.current && !event.repeat) begin(event.currentTarget, 'hold', direction ?? arrow!, undefined, event.key, event.shiftKey ? .1 : 1);
  };
  const keyUp = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (held.current?.key === event.key) { event.preventDefault(); event.stopPropagation(); stop(); }
  };
  const click = (direction: number, fine = false, detail = 0) => {
    if (detail > 0 || performance.now() < suppressClickUntil.current || latest.current.properties.disabled) return;
    latest.current.group.begin();
    const props = latest.current.properties;
    props.onChange(boundedValue(props.value + direction * props.step * (fine ? 1 : nudgeSteps(props)), props)); latest.current.group.end();
  };
  return { visual, pointerDown, pointerMove, release, keyDown, keyUp, click };
}
